import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
} from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

const SaveAsAutomationDialog = ({
  open,
  onClose,
  onSave,
  onOpen,
  automationName,
}) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  const [savedAutomationId, setSavedAutomationId] = useState(null);

  // Auto-start saving when dialog opens
  useEffect(() => {
    if (open && !saving && !saved && !error) {
      handleSave();
    }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setError(false);
    try {
      const automationId = await onSave();
      if (automationId) {
        setSavedAutomationId(automationId);
        setSaved(true);
      } else {
        setError(true);
      }
    } catch (error) {
      console.error("Save failed:", error);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleOpen = () => {
    if (savedAutomationId && onOpen) {
      onOpen(savedAutomationId);
    }
    handleClose();
  };

  const handleClose = () => {
    setSaving(false);
    setSaved(false);
    setError(false);
    setSavedAutomationId(null);
    onClose();
  };

  const handleRetry = () => {
    setError(false);
    handleSave();
  };

  return (
    <Dialog
      open={open}
      onClose={!saving ? handleClose : undefined}
      variant="pops"
    >
      <DialogTitle className="pb-1 pt-4 px-4">
        <Typography
          variant="h6"
          style={{
            fontSize: "1.25rem",
            fontWeight: 500,
            lineHeight: 1.6,
          }}
        >
          {saved
            ? "Automation Saved"
            : error
            ? "Save Failed"
            : "Saving Automation"}
        </Typography>
      </DialogTitle>

      <DialogContent className="px-4 py-2">
        <div className="flex flex-col items-center py-2 gap-2">
          {saving ? (
            <>
              <CircularProgress size={32} />
              <Typography
                variant="body2"
                color="text.secondary"
                style={{ textAlign: "center" }}
              >
                Saving as formal automation...
              </Typography>
            </>
          ) : saved ? (
            <>
              <CheckCircleIcon
                style={{
                  fontSize: 32,
                  color: "hsl(var(--primary))",
                }}
              />
              <Typography
                variant="body2"
                color="text.primary"
                style={{ textAlign: "center" }}
              >
                Successfully saved!
              </Typography>
            </>
          ) : error ? (
            <>
              <ErrorIcon
                style={{
                  fontSize: 32,
                  color: "hsl(var(--destructive))",
                }}
              />
              <Typography
                variant="body2"
                color="text.primary"
                style={{ textAlign: "center" }}
              >
                Failed to save automation.
              </Typography>
            </>
          ) : null}
        </div>
      </DialogContent>

      <DialogActions className="px-4 pb-4 pt-1 gap-2">
        {saving ? (
          <></>
        ) : saved ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              style={{
                textTransform: "none",
                minWidth: "70px",
                boxShadow: "none",
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleOpen}
              variant="contained"
              style={{
                textTransform: "none",
                minWidth: "70px",
                boxShadow: "none",
              }}
            >
              Open
            </Button>
          </>
        ) : error ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              style={{
                textTransform: "none",
                minWidth: "70px",
                boxShadow: "none",
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleRetry}
              variant="contained"
              style={{
                textTransform: "none",
                minWidth: "70px",
                boxShadow: "none",
              }}
            >
              Retry
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default SaveAsAutomationDialog;
